import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router-dom'

import { ChatWidget } from '@features/chat/views/ChatWidget'
import { useProfile } from '@services/users/queries/useProfile'

import { cn } from '@/lib/utils'

import { useIsMapRoute } from '@features/livability/utils/paths'

import { AppSidebar } from './AppSidebar'
import { AppTopbar } from './AppTopbar'
import {
  getNavItems,
  isLandlordRole,
  isUserRole,
  settingsNavItem
} from './navConfig'

export function AppLayout() {
  const { t } = useTranslation('layout')
  const { pathname } = useLocation()
  const { data: user } = useProfile(true)

  const role = user?.role
  const items = getNavItems(role, user)
  // Tenants and landlords get the floating support chat widget.
  const showChat = isUserRole(role) || isLandlordRole(role)

  // Page title from the active nav item (main items + settings). Flatten
  // dropdown groups so their child routes resolve a title too.
  const active = [...items, settingsNavItem]
    .flatMap((item) => (item.children ? [item, ...item.children] : [item]))
    .find(
      (item) =>
        !item.external &&
        (pathname === item.key || pathname.startsWith(item.key + '/'))
    )
  const title = active ? t(`navigation.${active.labelKey}`) : ''
  const isMapRoute = useIsMapRoute()

  return (
    <div className="flex min-h-screen w-full bg-background">
      <aside className="hidden w-72 shrink-0 border-r border-ink/[0.06] lg:block">
        <div className="sticky top-0 h-screen">
          <AppSidebar items={items} user={user} />
        </div>
      </aside>

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col',
          isMapRoute && 'h-svh overflow-hidden'
        )}
      >
        <AppTopbar title={title} user={user} items={items} />
        <main
          className={cn(
            'flex min-h-0 flex-1 flex-col',
            isMapRoute
              ? 'overflow-hidden'
              : 'px-4 pt-6 lg:px-8 lg:pt-8',
            showChat && !isMapRoute ? 'pb-28 lg:pb-28' : isMapRoute ? 'pb-0' : 'pb-6 lg:pb-8'
          )}
        >
          <div
            className={cn(
              'flex min-h-0 w-full flex-1 flex-col',
              !isMapRoute && 'mx-auto max-w-[1200px]'
            )}
          >
            <Outlet />
            {showChat && !isMapRoute && <ChatWidget />}
          </div>
        </main>
      </div>
    </div>
  )
}
